// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import * as api from '../../api/client'

/**
 * THE opener for a therapy's patient-facing documents. Every "Enrollment PDF" / "Consent PDF"
 * control in CareRx goes through here — there is no second way to open one.
 *
 * WHY (Mario 2026-09-02): the document tracker on the Consent tab was opening
 * `/carerx/doc-templates/{kind}/pdf` — the ACTIVE UPLOADED BLOB out of esign_doc_templates. That is
 * exactly the mechanism D-CRX-63 ("generate the patient consent, stop shipping a file") and
 * D-CRX-67 (spec-driven enrollment form) replaced: it is not this patient's document, it carries
 * whatever letterhead and whatever state's law the uploaded file happened to have, and on a tenant
 * with nothing uploaded it 404s `template_missing`. Staff read that as "the PDFs are random".
 *
 * Both kinds are THERAPY-grain, SERVER-rendered from the tenant's own data, and there is exactly
 * ONE renderer each:
 *   enrollment — EnrollmentPacketComposer + CareRxEnrollmentPdf
 *   consent    — CareRxConsentDoc, from dbo.pharmacies branding + the licensing state's clause
 *                pack + the therapy's patient/Rx/claim data
 *
 * Both open watermarked DRAFTS. Neither is for signature: the enrollment form is gated on clinical
 * review completing, and the consent's real copy is the one the e-sign lane sends.
 */
export type TherapyDocKind = 'enrollment' | 'consent'

export type TherapyDocBrand = { name?: string | null; logo_url?: string | null }

export const THERAPY_DOC_LABEL: Record<TherapyDocKind, string> = {
  // Both are server-generated PDFs, so both say PDF.
  enrollment: 'Enrollment PDF',
  consent: 'Consent PDF',
}

/**
 * Open the generated draft document for one therapy.
 *
 * ONE METHOD PER DOCUMENT (owner, 2026-09-06). The enrollment form had TWO — this opener's
 * pdf-lib template fill, and the packet — while DocuSign quietly used a THIRD (a blank uploaded
 * blob). The pdf-lib path is gone: it filled a fixed-geometry AcroForm, so it could only ever
 * print three baseline-lab rows whatever the drug record required, and every mapping miss was
 * swallowed by a silent catch. Deleting it also drops ~200 KB of pdf-lib + base64 template from
 * the bundle.
 *
 * BOTH kinds arrive the same way: the server composes and renders the PDF, the browser opens the
 * bytes. The React "packet" preview that briefly stood beside this is GONE — it rendered the same
 * model through a second renderer and drifted within days: six sections (screening rules, holds,
 * the drug record, facility, safety constraints, the rate ladder) were added to the PDF and never
 * to it, so the same therapy looked different depending on which button you pressed. One document,
 * one renderer, one artifact — for preview, for the chart and for DocuSign.
 *
 * Both claim their tab INSIDE the click gesture (a window.open after an await is popup-blocked
 * silently, which reads to the operator as "the button does nothing"), so call this directly from
 * the handler. A caller that must look something up FIRST — a patient-grain surface resolving
 * which therapy it means — claims the tab itself with `claimDocumentTab()` and passes it as
 * `target`, so the gesture is not spent by the time the document is ready.
 */
export async function openTherapyDocument(
  kind: TherapyDocKind, therapyId: string, _brand?: TherapyDocBrand, target?: Window | null,
): Promise<void> {
  if (kind === 'enrollment') return api.openCareRxEnrollmentPdf(therapyId, { target })
  return api.openCareRxConsentPdf(therapyId, { target })
}

/** Claim a tab inside a click gesture, before any await. Null when the browser blocked it. */
export function claimDocumentTab(kind: TherapyDocKind): Window | null {
  const w = window.open('', '_blank')
  w?.document.write(`<title>${THERAPY_DOC_LABEL[kind]}…</title>`
    + '<p style="font-family:sans-serif;padding:24px;color:#334155">Finding the therapy…</p>')
  return w
}

/**
 * Resolve which therapy a patient-grain surface means, then open the document for it.
 *   one therapy   -> open it
 *   several       -> hand them back so the caller can ask (never guess: the wrong drug on a
 *                    consent or enrollment form is a clinical document that is wrong on paper)
 *   none          -> throw with the reason; there is nothing to render, and inventing a blank
 *                    form would produce an artifact that looks like a draft but is not one
 */
export async function resolveTherapyForDocument(
  patientId: string, kind: TherapyDocKind,
): Promise<{ therapyId: string } | { choices: api.CareRxTherapyLane[] }> {
  const { therapies } = await api.listCareRxTherapies(patientId)
  if (therapies.length === 0)
    throw new Error(`No CareRx therapy for this patient. The ${kind === 'consent' ? 'patient consent' : 'enrollment form'} `
      + 'is generated per therapy — start one from the therapy lanes, then the document has a drug to describe.')
  if (therapies.length === 1) return { therapyId: therapies[0].id }
  return { choices: therapies }
}
